import '../styles/BaseLocalesForm.scss';

import { CopyIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert } from '@whistler-ui/k2/components/Alert';
import { Button } from '@whistler-ui/k2/components/Button';
import { Checkbox } from '@whistler-ui/k2/components/Checkbox';
import { Form } from '@whistler-ui/k2/components/Form';
import { InputField } from '@whistler-ui/k2/components/InputField';
import {
  PlanType,
  PlanTypeToggle,
} from '@whistler-ui/k2/components/PlanTypeToggle';
import { Skeleton } from '@whistler-ui/k2/components/Skeleton';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { TextEditor } from '@whistler-ui/k2/components/TextEditor';
import { Tooltip } from '@whistler-ui/k2/components/Tooltip';
import { Heading, Paragraph } from '@whistler-ui/k2/components/Typography';
import {
  cn,
  deepMergeExcludeKeys,
  isFieldError,
} from '@whistler-ui/k2/helpers';
import { scrollTop } from '@whistler-ui/k2/helpers/common';
import { schemaIsRequired } from '@whistler-ui/k2/helpers/objectUtils';
import { getUserFacingErrorMessage } from '@whistler-ui/k2/helpers/userFacingError';
import { useAppMessages, useClassHelper } from '@whistler-ui/k2/hooks';
import { useFormik } from 'formik';
import i18next from 'i18next';
import {
  FormEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useUnmount } from 'react-use';
import * as yup from 'yup';

import { useUpdateLocalisation } from '../../../hooks';
import { useWebsheetConfigStore } from '../../../store';
import { BaseLocalesFormPropsType } from '../../../types';
import { handleActionType } from '../helpers/formEvents';
import { buildCopiedValues, pairPrefixGroups } from '../helpers/prefixGroups';
import { ActionButtons } from './ActionButtons';

const componentName = 'c-BaseLocalesForm';

const getDefaultArraySchema = (schema: any) => {
  return TextEditor.CustomSchemaValidationFactory(schema);
};

export const BaseLocalesForm = ({
  data,
  type,
  currentPageIndex,
  defaultData,
  brandId,
  tag,
  environmentId,
  locale,
  canGoToNextLocalisation,
  goNextLocalisation,
  canGoToPrevLocalisation,
  goPrevLocalisation,
  callbacks,
  prefixGroups,
  prefixGroupsLoading,
}: BaseLocalesFormPropsType) => {
  const c = useClassHelper(componentName);
  const { t } = useTranslation('components');
  const [shouldForward, setShouldForward] = useState(false);
  const { setUpdatePreviewType, setSelectedField, updateLocalisationPreview } =
    useWebsheetConfigStore();

  const queryClient = useQueryClient();

  const { mutateAsync: updateLocalisation } = useUpdateLocalisation();

  const [setMessages] = useAppMessages();

  const defaultValues = useMemo<{ [key: string]: unknown }>(() => {
    const values = Object.keys(defaultData[type]).reduce((acc, key) => {
      acc[key] = defaultData[type][key].defaultValue;
      return acc;
    }, {});

    const mergedValues = deepMergeExcludeKeys(values, data?.[type]);

    return mergedValues;
  }, [defaultData, data, type]);

  const validationSchema = useMemo(() => {
    return Object.keys(defaultData[type]).reduce((acc, key) => {
      const schema = defaultData?.[type]?.[key]?.validationSchema;
      const isArray = schema?.type === 'array';
      const isRequired = schemaIsRequired(schema);

      if (isArray && isRequired) {
        acc[key] = getDefaultArraySchema(schema);
        return acc;
      }

      if (schema) {
        acc[key] = schema;
      }

      return acc;
    }, {});
  }, [defaultData, type]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: defaultValues,
    validationSchema: yup.object().shape(validationSchema),
    onSubmit: async values => {
      const localisation = {
        data: {
          ...data,
          [type]: values,
        },
        brand_id: brandId,
        locale: locale,
      };

      try {
        await updateLocalisation({
          localisation,
          tag: tag,
          brandId: brandId,
          environmentId: environmentId,
        });

        await queryClient.invalidateQueries({
          queryKey: ['get_websheet_settings_by_brand_id'],
        });

        setMessages({
          type: useAppMessages.type.SUCCESS,
          msg: `Localisation saved successfully`,
        });
      } catch (error) {
        setMessages({
          type: useAppMessages.type.ERROR,
          msg: getUserFacingErrorMessage(error, 'common:errors.default'),
        });
        return;
      }

      if (shouldForward) {
        scrollTop({
          behavior: 'smooth',
        });

        if (canGoToNextLocalisation && goNextLocalisation) {
          goNextLocalisation();
          return;
        }

        callbacks.goForward();
      }
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    errors,
    touched,
    isSubmitting,
    isValid,
    submitCount,
  } = formik;

  const isFieldErrorCallback = useCallback(
    (field: string) => {
      return isFieldError(field, touched, errors);
    },
    [touched, errors],
  );

  const groupedFields = useMemo(
    () =>
      Object.keys(defaultData[type]).reduce((acc, key) => {
        const group = defaultData[type][key].group || 'default';
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push(key);
        return acc;
      }, {}),
    [defaultData, type, errors],
  );

  // Pairs detected for this tab's section (empty when `prefixGroups` is
  // absent or no group matches — the flat/legacy render path is untouched
  // in that case). Structural, not `errors`-dependent: pairing shape never
  // changes on validation, unlike `groupedFields` above.
  const prefixPairs = useMemo(
    () =>
      prefixGroups?.length
        ? pairPrefixGroups(defaultData[type], prefixGroups)
        : [],
    [defaultData, type, prefixGroups],
  );

  const pairByBaseGroup = useMemo(
    () => new Map(prefixPairs.map(pair => [pair.baseGroup, pair])),
    [prefixPairs],
  );

  const variantGroupNames = useMemo(
    () => new Set(prefixPairs.map(pair => pair.variantGroup)),
    [prefixPairs],
  );

  const fieldsSectionRef = useRef<HTMLElement>(null);

  // While the gating feature flag resolves, the variant groups may not be in
  // the (already filtered) template yet — hold the toggle's spot with a
  // skeleton instead of letting it pop in after the flag loads.
  const showToggleSkeleton = Boolean(
    prefixGroupsLoading && prefixGroups?.length,
  );

  // Single tab-level toggle state (decision 6): one PlanTypeToggle drives
  // every matched pair in the tab together — no per-pair independence.
  // Defaults to the base side ('one_number').
  const [planType, setPlanType] = useState<PlanType>('one_number');

  const handleTabToggle = useCallback(
    (next: PlanType) => {
      setPlanType(next);
      // First pair in declaration order represents the whole tab for the
      // preview flip, even though every pair swaps together.
      setSelectedField(
        next === 'family_plan' && prefixPairs[0]
          ? { field: prefixPairs[0].variantFieldKeys[0], type }
          : null,
      );
    },
    [setSelectedField, type, prefixPairs],
  );

  const handleValidateSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const { shouldForward } = handleActionType(event);
    setShouldForward(shouldForward);

    handleSubmit(event);

    // handleSubmit blocks invalid submissions silently; re-validate here to
    // know whether THIS attempt failed, and if so bring the validation alert
    // into view. Two scrollers on this page: the window AND the fields
    // section (overflow-y: auto of its own) — both must go up or the alert
    // stays out of sight.
    const validationErrors = await formik.validateForm();
    const errorFields = Object.keys(validationErrors);

    if (errorFields.length === 0) {
      return;
    }

    // Any invalid submit scrolls to top, with or without plan pairs — the
    // first inline errors (or the alert, when pairs exist) live up there.
    fieldsSectionRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    scrollTop({ behavior: 'smooth' });

    // Alert and tab auto-switch only exist when the tab has plan pairs.
    if (prefixPairs.length === 0) {
      return;
    }

    // Jump to the side the errors live on so they show inline (no field
    // focus). Base wins when BOTH sides are invalid; errors on unpaired
    // groups (visible from either side) never force a switch.
    const hasBaseError = prefixPairs.some(pair => {
      return (groupedFields[pair.baseGroup] ?? []).some((key: string) =>
        errorFields.includes(key),
      );
    });
    const hasVariantError = prefixPairs.some(pair => {
      return pair.variantFieldKeys.some(key => errorFields.includes(key));
    });

    let errorSide: PlanType | null = null;
    if (hasBaseError) {
      errorSide = 'one_number';
    } else if (hasVariantError) {
      errorSide = 'family_plan';
    }

    if (errorSide && errorSide !== planType) {
      handleTabToggle(errorSide);
    }
  };

  // Config-driven copy action. The button lives on the variant tab of the
  // prefix config it is nested in (first pair carries the config, same
  // convention as toggleLabels) and overwrites every variant field of every
  // pair on this tab with its `source` counterpart's current value.
  const copyContent = prefixPairs[0]?.copyContent;

  const handleCopyContent = useCallback(() => {
    if (!copyContent) {
      return;
    }

    setValues(current => ({
      ...current,
      ...buildCopiedValues(current, prefixPairs, copyContent.source),
    }));

    setMessages({
      type: useAppMessages.type.SUCCESS,
      msg: copyContent.successMessage,
    });
  }, [copyContent, prefixPairs, setValues, setMessages]);

  // Per-side aggregate error flags for the toggle items — true if ANY field
  // belonging to that side (across every matched pair) has an error AND was
  // touched. Gated by `touched` so the icon can never disagree with the
  // form: it flags exactly the errors the inline messages would show
  // (`isFieldError` is touched-based too).
  const sideErrors = useMemo(() => {
    const isVisibleError = (key: string) => {
      return Boolean(touched?.[key]) && Boolean(errors?.[key]);
    };

    return prefixPairs.reduce(
      (acc, pair) => ({
        base:
          acc.base ||
          (groupedFields[pair.baseGroup] ?? []).some(isVisibleError),
        variant: acc.variant || pair.variantFieldKeys.some(isVisibleError),
      }),
      { base: false, variant: false },
    );
  }, [prefixPairs, groupedFields, errors, touched]);

  // Mirrors PlanTypeToggle's render rule: a side's icon shows whenever that
  // side has touched errors, selected or not.
  const hasSideErrors = sideErrors.base || sideErrors.variant;

  // Submit is never blocked by validity (both save buttons stay enabled);
  // instead the alert above the PlanTypeToggle surfaces the failure. It
  // shows in SYNC with the tab error icons (touched errors on either side —
  // no submit needed) and also after any failed submit attempt. Auto-clears
  // once the form becomes valid. Only meaningful when the tab has plan
  // pairs (family plan feature on for this template).
  const showValidationAlert =
    prefixPairs.length > 0 && ((submitCount > 0 && !isValid) || hasSideErrors);

  useEffect(() => {
    updateLocalisationPreview({
      brandId: brandId,
      tag: tag,
      environmentId: environmentId,
      type,
      ...values,
    });

    return () => {
      setUpdatePreviewType(type);
    };
  }, [values, type, brandId, tag, environmentId]);

  useUnmount(() => {
    setSelectedField(null);
  });

  const handleGoBackward = () => {
    scrollTop({
      behavior: 'smooth',
    });

    if (canGoToPrevLocalisation && goPrevLocalisation) {
      goPrevLocalisation();
      return;
    }

    callbacks.goBackward();
  };

  // Renders a single field's input control. Reused unmodified for both the
  // legacy flat/grouped path and each side of a matched prefix-group pair —
  // only the surrounding grouping/heading layer differs between the two.
  const renderField = useCallback(
    (field: string) => {
      const isRequired = schemaIsRequired(
        defaultData[type][field]?.validationSchema,
      );

      const isTextEditorType =
        Array.isArray(defaultData[type][field].defaultValue) ||
        defaultData[type][field]?.type === 'richText';

      const isStringType =
        defaultData[type][field]?.type === 'string' ||
        typeof defaultData[type][field].defaultValue === 'string';

      const isBoolean =
        defaultData[type][field]?.type === 'boolean' ||
        typeof defaultData[type][field].defaultValue === 'boolean';

      const errorMessage = errors?.[field] ? t(errors[field] as string) : '';

      // Field-level tooltip side override (e.g. the cross-plan notification
      // tooltips open left); falls back to the legacy per-kind defaults.
      const tooltipAlign = defaultData[type][field]?.tooltipAlign;

      return (
        <div key={field} className={`form__fields form__fields--${field}`}>
          {/* if data type is Array */}
          {isTextEditorType && (
            <Fragment>
              <div className="flex items-center gap-2">
                <InputField.Label id={field} required={isRequired}>
                  {t(`base_locales_form.inputs.${type}.${field}.label`)}
                </InputField.Label>
                {i18next.exists(
                  `components:base_locales_form.inputs.${type}.${field}.tooltip`,
                ) && (
                  <span className="block mb-[8px]">
                    <Tooltip
                      color="#3a474e"
                      weight="regular"
                      align={tooltipAlign ?? 'bottom'}
                      size={16}
                      label={t(
                        `base_locales_form.inputs.${type}.${field}.tooltip`,
                      )}
                      contentClassName={c('__tooltip')}
                    />
                  </span>
                )}
              </div>
              <TextEditor
                required={isRequired}
                name={field}
                viewOnly={false}
                onChange={handleChange}
                onBlur={handleBlur}
                value={(values?.[field] as any[]) || []}
                onFocus={() => setSelectedField({ field, type })}
                error={isFieldErrorCallback(field)}
                errorMessage={errorMessage}
                placeholder={
                  i18next.exists(
                    `components:base_locales_form.inputs.${type}.${field}.placeholder`,
                  )
                    ? t(`base_locales_form.inputs.${type}.${field}.placeholder`)
                    : undefined
                }
              />
            </Fragment>
          )}

          {/* if data type is a string */}
          {isStringType && (
            <InputField
              label={
                <div className="flex items-center gap-2">
                  <InputField.Label id={field} required={isRequired}>
                    {t(`base_locales_form.inputs.${type}.${field}.label`)}
                  </InputField.Label>
                  {i18next.exists(
                    `components:base_locales_form.inputs.${type}.${field}.tooltip`,
                  ) && (
                    <span className="block mb-[8px]">
                      <Tooltip
                        color="#3a474e"
                        weight="regular"
                        align={tooltipAlign ?? 'right'}
                        size={16}
                        label={t(
                          `base_locales_form.inputs.${type}.${field}.tooltip`,
                        )}
                        contentClassName={c('__tooltip')}
                      />
                    </span>
                  )}
                </div>
              }
              type="text"
              id={field}
              name={field}
              onBlur={handleBlur}
              touched={touched?.[field]?.toString?.()}
              value={values[field] as string}
              onChange={handleChange}
              placeholder={t(
                `base_locales_form.inputs.${type}.${field}.placeholder`,
              )}
              autoComplete="off"
              onFocus={() => setSelectedField({ field, type })}
              required={isRequired}
              error={isFieldErrorCallback(field)}
              errorMessage={errorMessage}
            />
          )}

          {isBoolean && (
            <Stack direction="row" gap={2}>
              <InputField.Label id={field} required={isRequired}>
                {t(`base_locales_form.inputs.${type}.${field}.label`)}
              </InputField.Label>
              <Checkbox
                id={field}
                name={field}
                onChange={handleChange}
                isChecked={values[field] as boolean}
              />
            </Stack>
          )}

          {/* Optional helper text below the input — declared per field in the
              i18n entry (label/tooltip/placeholder/description), any kind. */}
          {i18next.exists(
            `components:base_locales_form.inputs.${type}.${field}.description`,
          ) && (
            <Paragraph size="sm" color="light" className="mt-2!">
              {t(`base_locales_form.inputs.${type}.${field}.description`)}
            </Paragraph>
          )}
        </div>
      );
    },
    [
      defaultData,
      type,
      values,
      errors,
      touched,
      isFieldErrorCallback,
      handleChange,
      handleBlur,
      setSelectedField,
      t,
      c,
    ],
  );

  const fields = useMemo(() => {
    return Object.keys(groupedFields).map(groupKey => {
      // Variant groups render at their base's slot (below) — skip their
      // standalone position entirely.
      if (variantGroupNames.has(groupKey)) {
        return null;
      }

      const pair = pairByBaseGroup.get(groupKey);
      // Unpaired groups always show `groupedFields[groupKey]`, unaffected by
      // `planType` (LPTT-22). Paired groups swap in place: the base's own
      // slot renders either its own fields or its pair's variant fields,
      // depending on the single tab-level toggle.
      const isVariantVisible = Boolean(pair && planType === 'family_plan');
      const visibleFieldKeys = isVariantVisible
        ? pair!.variantFieldKeys
        : groupedFields[groupKey];

      // Heading (and description) follow the VISIBLE side — the variant
      // group carries its own i18n key (e.g. family_plan_selection →
      // "Family Watch Plan Selection").
      const headingGroupKey = isVariantVisible ? pair!.variantGroup : groupKey;

      const fieldset = visibleFieldKeys.map(renderField);
      const isGrouped = groupKey !== 'default';

      return (
        <Fragment key={groupKey}>
          {isGrouped ? (
            <div className={cn(c('__fieldset'), 'mb-8!')}>
              <Heading level={2}>
                {t(`base_locales_form.groups.${headingGroupKey}`)}
              </Heading>
              {i18next.exists(
                `components:base_locales_form.group_descriptions.${headingGroupKey}`,
              ) && (
                <Paragraph className="mb-6!">
                  {t(`base_locales_form.group_descriptions.${headingGroupKey}`)}
                </Paragraph>
              )}
              <div className={c('__fieldset-fields')}>{fieldset}</div>
            </div>
          ) : (
            fieldset
          )}
        </Fragment>
      );
    });
  }, [
    groupedFields,
    variantGroupNames,
    pairByBaseGroup,
    planType,
    renderField,
    t,
    c,
  ]);

  return (
    <section className={componentName}>
      <Form
        formClassName={c('__form-wrapper')}
        className={`default-form ${c('__form')}`}
        onSubmit={handleValidateSubmit}
        loading={isSubmitting}
      >
        <section className={c('__fields')} ref={fieldsSectionRef}>
          {showToggleSkeleton && (
            <div className="col-span-full">
              <Skeleton width="100%" height={44} />
            </div>
          )}
          {!showToggleSkeleton && prefixPairs.length > 0 && (
            <Stack
              direction="column"
              gap={1}
              className="col-start-1 col-span-6 mb-"
            >
              {showValidationAlert && (
                <Alert
                  variant="error"
                  showCopyButton={false}
                  className="mb-5"
                  data-testid="base-locales-validation-alert"
                  title={t('base_locales_form.validation_alert.content')}
                />
              )}
              <div className="px-1">
                <PlanTypeToggle
                  value={planType}
                  onChange={handleTabToggle}
                  oneNumberLabel={prefixPairs[0]?.toggleLabels?.base}
                  familyPlanLabel={prefixPairs[0]?.toggleLabels?.variant}
                  oneNumberError={sideErrors.base}
                  familyPlanError={sideErrors.variant}
                  disabled={isSubmitting}
                  fullWidth
                />
              </div>
              {planType === 'family_plan' && copyContent && (
                <Stack
                  direction="row"
                  gap={1}
                  align="center"
                  className="group px-1"
                >
                  <Button
                    color="ghost"
                    onClick={handleCopyContent}
                    disabled={isSubmitting}
                    classes={{
                      button: 'px-0!',
                    }}
                  >
                    <Stack direction="row" gap={2} align="center">
                      <CopyIcon size={16} />
                      <span className="group-hover:underline">
                        {copyContent.label}
                      </span>
                    </Stack>
                  </Button>
                  <Tooltip
                    color="#3a474e"
                    weight="regular"
                    align="bottom"
                    size={16}
                    label={copyContent.tooltip}
                    contentClassName={c('__tooltip')}
                  />
                </Stack>
              )}
            </Stack>
          )}
          {fields}
        </section>

        <ActionButtons
          isLoading={isSubmitting}
          currentPageIndex={currentPageIndex}
          goBackward={handleGoBackward}
          disabledSaveButton={false}
        />
      </Form>
    </section>
  );
};
